import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import ThemeToggle from "./ThemeToggle";

export default function PrivacyPolicy() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Navbar */}
      <header
        className={`pt-safe sticky inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 shadow-sm backdrop-blur-md dark:bg-gray-950/80"
            : "bg-transparent"
        }`}
      >
        <nav
          aria-label="Main"
          className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8"
        >
          {/* Logo */}
          <a
            href="#top"
            className="flex items-center gap-3 font-bold text-gray-900 dark:text-white"
          >
            <img
              src="/favicon-browser.svg"
              alt="Pantry logo"
              className="h-8 w-8"
            />
            <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Pantry
            </span>
          </a>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back Home
            </Link>
            <ThemeToggle />
          </div>
        </nav>
      </header>

      {/* Page content */}
      <main
        id="main-content"
        className="mx-auto max-w-7xl px-4 pt-14 pb-20 sm:px-6 sm:pt-18 lg:px-8"
      >
        {/* Page header */}
        <div className="mb-10 text-center">
          <p className="mb-1 text-sm font-semibold tracking-widest text-blue-600 uppercase dark:text-blue-400">
            Legal
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Effective date: August 18, 2026
          </p>
        </div>

        <div className="space-y-4">
          <Section title="1. Introduction">
            <p>
              Welcome to{" "}
              <strong className="text-gray-900 dark:text-white">Pantry</strong>,
              developed by{" "}
              <strong className="text-gray-900 dark:text-white">
                Hillard Works LLC
              </strong>
              . Pantry is a household kitchen management app available at{" "}
              <ExtLink href="https://pantry.hillardworks.com">
                pantry.hillardworks.com
              </ExtLink>{" "}
              and{" "}
              <ExtLink href="https://play.google.com/store/apps/details?id=com.hillardworks.pantry">
                Google Play
              </ExtLink>
              . This Privacy Policy explains what information we collect, how we
              use it, and your rights regarding that information.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p className="mb-3">
              When you use Pantry, we collect the following information:
            </p>
            <BulletList
              items={[
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Email address
                  </strong>{" "}
                  — used for account creation, authentication, invitation
                  emails, and license management.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Password
                  </strong>{" "}
                  — your password is hashed by Supabase Auth before storage; we
                  never store or transmit your plaintext password.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Household name
                  </strong>{" "}
                  — a label you provide to identify your household group.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Inventory items
                  </strong>{" "}
                  — name, quantity, unit, storage location (fridge, freezer,
                  pantry, or other), and category of items you track.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Shopping list items
                  </strong>{" "}
                  — name, quantity, unit, and category.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Favorite recipes
                  </strong>{" "}
                  — a list of recipes you save to your favorites collection.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    License status
                  </strong>{" "}
                  — whether you hold a web or mobile app license, linked to your
                  email address. We do not store payment card numbers or billing
                  addresses.
                </>,
              ]}
            />
            <p className="mt-4">
              Additionally, your active household selection and session tokens
              are stored locally in your browser's{" "}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-800">
                localStorage
              </code>
              . Recipe search results are cached in{" "}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-800">
                sessionStorage
              </code>{" "}
              for performance and are not shared with any analytics service.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <BulletList
              items={[
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Provide the service
                  </strong>{" "}
                  — sync your inventory, shopping list, and recipes across all
                  members of your household in real time.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Transactional emails
                  </strong>{" "}
                  — send household invitations, password reset links, and signup
                  confirmation messages.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    License management
                  </strong>{" "}
                  — process and verify license purchases so you can access the
                  app.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Abuse protection
                  </strong>{" "}
                  — Cloudflare Turnstile challenges on authentication forms help
                  prevent automated abuse.
                </>,
              ]}
            />
            <p className="mt-4">
              We do not use your data for advertising, profiling, or any purpose
              beyond operating and improving the service.
            </p>
          </Section>

          <Section title="4. Data Sharing">
            <p className="mb-3">
              We do not sell your personal data. We share data only with the
              following service providers, solely to operate the app:
            </p>
            <BulletList
              items={[
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Supabase
                  </strong>{" "}
                  — backend-as-a-service provider hosting our database,
                  authentication, edge functions, and realtime infrastructure.
                  Your data resides on Supabase servers.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Stripe
                  </strong>{" "}
                  — payment processor for web license purchases. Stripe receives
                  only what is required to complete the transaction; we never
                  store card numbers or billing addresses.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Spoonacular
                  </strong>{" "}
                  — recipe search API. Ingredient lists are forwarded
                  server-side via a Supabase edge function; your browser never
                  communicates directly with Spoonacular, and requests are not
                  tied to your identity.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Resend
                  </strong>{" "}
                  — transactional email delivery (invitations, password resets,
                  signup confirmations).
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Cloudflare
                  </strong>{" "}
                  — Turnstile bot-protection challenges on authentication forms.
                </>,
                <>
                  <strong className="text-gray-900 dark:text-white">
                    Netlify
                  </strong>{" "}
                  — frontend hosting for the web app.
                </>,
              ]}
            />
          </Section>

          <Section title="5. Data Storage &amp; Security">
            <p>
              Your data is stored on Supabase infrastructure and transmitted
              exclusively over HTTPS. We use Supabase's Row Level Security (RLS)
              policies to ensure that each user can only access data belonging
              to their own household. Passwords are never stored in plaintext;
              they are hashed by Supabase Auth before any storage occurs.
            </p>
          </Section>

          <Section title="6. Data Retention &amp; Deletion">
            <p className="mb-3">
              You can delete your account at any time from within the app
              (Settings → "Delete My Account"). Doing so permanently deletes
              your authentication record and removes you from all household
              memberships.
            </p>
            <p className="mb-3">
              A record in our{" "}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-800">
                purchased_licenses
              </code>{" "}
              table — containing only your email address and license status — is
              retained after account deletion. This allows your license to be
              restored automatically if you re-register with the same email
              address in the future.
            </p>
            <p>
              If you would like to request deletion of your license record or
              any other personal data, please contact us (see Section 9).
            </p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>
              Pantry is not directed at children under the age of 13. We do not
              knowingly collect personal information from children under 13. If
              you believe a child has provided us with personal information,
              please contact us and we will delete it promptly.
            </p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes
              will be posted on this page with an updated date. Continued use of
              the app after changes are posted constitutes acceptance of the
              updated policy.
            </p>
          </Section>

          <Section title="9. Contact">
            <p className="mb-3">
              If you have questions or concerns about this Privacy Policy, or
              wish to exercise your data rights, please contact:
            </p>
            <p>
              <strong className="text-gray-900 dark:text-white">
                Hillard Works LLC
              </strong>
              <br />
              <a
                href="mailto:contact@hillardworks.com"
                className="text-blue-600 underline transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                contact@hillardworks.com
              </a>
            </p>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex items-center gap-2.5">
              <a
                href="https://www.hillardworks.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Hillard Works LLC (opens in new tab)"
                className="flex items-center gap-3 text-sm font-bold text-gray-500 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 10 Q4 4 10 4 L90 4 Q96 4 96 10 L96 63.66 L63.66 96 L10 96 Q4 96 4 90 Z"
                    fill="none"
                    className="stroke-gray-900 dark:stroke-stone-50"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10 14 Q10 10 14 10 L86 10 Q90 10 90 14 L90 61.32 L61.32 90 L14 90 Q10 90 10 86 Z"
                    className="fill-gray-900 dark:fill-stone-50"
                  />
                </svg>
                <div className="leading-tight">Hillard Works LLC</div>
              </a>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-600">
              &copy; {new Date().getFullYear()} Hillard Works LLC. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900">
      <h2 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
        {title}
      </h2>
      <div className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2">
      {React.Children.map(items, (item) => (
        <li className="flex gap-2">
          <span className="mt-1 h-1.5 w-1.5 shrink-0 translate-y-1 rounded-full bg-blue-500" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ExtLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
    >
      {children}
      <span className="sr-only"> (opens in new tab)</span>
    </a>
  );
}
